import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Fuel, HardHat, Wheat, Truck, Check } from 'lucide-react'
import { services } from '@data/company'
import AnimatedSection from '@components/AnimatedSection'

const iconMap: Record<string, React.ElementType> = {
  Fuel, HardHat, Wheat, Truck
}

export default function ServicesPage() {
  const [activeService, setActiveService] = useState(0)
  const currentService = services[activeService]
  const IconComponent = iconMap[currentService.icon] || Fuel

  return (
    <>
      {/* Page Header */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-martimma-yellow/10 to-transparent" />
        <div className="section-padding max-w-7xl mx-auto relative z-10">
          <AnimatedSection>
            <span className="text-martimma-yellow text-sm font-semibold tracking-wider uppercase">Our Services</span>
            <h1 className="heading-xl mt-4 mb-6">Four Sectors. One Integrated Vision.</h1>
            <p className="body-lg max-w-3xl">
              From energy supply to infrastructure development, agricultural solutions to logistics — we deliver comprehensive services that drive Nigeria's economic growth.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Service Tabs & Content */}
      <section className="py-12 relative">
        <div className="section-padding max-w-7xl mx-auto">
          {/* Tab Navigation */}
          <AnimatedSection>
            <div className="flex flex-wrap gap-3 mb-12">
              {services.map((service, index) => {
                const Icon = iconMap[service.icon] || Fuel
                return (
                  <button
                    key={service.id}
                    onClick={() => setActiveService(index)}
                    className={`flex items-center gap-3 px-6 py-4 rounded-xl font-medium transition-all duration-300 ${
                      activeService === index
                        ? 'bg-martimma-red text-white shadow-lg shadow-martimma-red/25'
                        : 'glass-card text-gray-400 hover:text-white hover:border-martimma-yellow/30'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="hidden sm:inline">{service.title}</span>
                  </button>
                )
              })}
            </div>
          </AnimatedSection>

          {/* Active Service Content */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentService.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                {/* Content */}
                <div>
                  <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 ${
                    currentService.color === 'red' ? 'bg-martimma-red/10' : 'bg-martimma-yellow/10'
                  }`}>
                    <IconComponent className={`w-8 h-8 ${
                      currentService.color === 'red' ? 'text-martimma-red' : 'text-martimma-yellow'
                    }`} />
                  </div>

                  <h2 className="heading-lg text-white mb-2">{currentService.title}</h2>
                  <p className="text-martimma-yellow font-medium mb-6">{currentService.subtitle}</p>
                  <p className="text-gray-300 leading-relaxed mb-8">{currentService.description}</p>

                  <div className="space-y-3">
                    {currentService.features.map((feature, i) => (
                      <motion.div
                        key={feature}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.05 }}
                        className="flex items-center gap-3"
                      >
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                          currentService.color === 'red' ? 'bg-martimma-red/20' : 'bg-martimma-yellow/20'
                        }`}>
                          <Check className={`w-3.5 h-3.5 ${
                            currentService.color === 'red' ? 'text-martimma-red' : 'text-martimma-yellow'
                          }`} />
                        </div>
                        <span className="text-gray-300 text-sm">{feature}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* Visual Placeholder */}
                <div className="relative">
                  <div className={`aspect-[4/3] rounded-2xl overflow-hidden border border-white/10 ${
                    currentService.color === 'red' 
                      ? 'bg-gradient-to-br from-martimma-red/20 to-martimma-dark' 
                      : 'bg-gradient-to-br from-martimma-yellow/20 to-martimma-dark'
                  }`}>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <IconComponent className={`w-24 h-24 mx-auto mb-4 opacity-20 ${
                          currentService.color === 'red' ? 'text-martimma-red' : 'text-martimma-yellow'
                        }`} />
                        <p className="text-gray-500 text-sm">[Service Illustration]</p>
                        <p className="text-gray-600 text-xs mt-1">Replace with actual image</p>
                      </div>
                    </div>
                    {/* Decorative elements */}
                    <div className="absolute top-4 right-4 w-20 h-20 border border-white/5 rounded-full" />
                    <div className="absolute bottom-4 left-4 w-32 h-32 border border-white/5 rounded-full" />
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-martimma-dark-light">
        <div className="section-padding max-w-7xl mx-auto">
          <AnimatedSection className="text-center mb-16">
            <span className="text-martimma-red text-sm font-semibold tracking-wider uppercase">Why Martimma</span>
            <h2 className="heading-lg mt-4">Six Reasons We're the Partner of Choice</h2>
          </AnimatedSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: "Multi-Sector Integrated Capability", desc: "Single partner across energy, construction, agriculture, and logistics — minimizing complexity for clients." },
              { title: "Strategic National Footprint", desc: "Headquarters in Lekki Lagos plus operational hubs in Warri and Benin City reaching all 6 geopolitical zones." },
              { title: "Modern Fleet & Infrastructure", desc: "GPS-tracked tankers, 50-truck loading gantries, automated terminal systems." },
              { title: "Local Content Champion", desc: "95% Nigerian content target — investing in local talent, suppliers, and communities." },
              { title: "Premium Quality & Compliance", desc: "ISO 9001:2015 aligned, DPR specified, NESREA compliant — verifiable standards." },
              { title: "Customer-Obsessed Service", desc: "Reliability, cleanliness, safety — recognized by clients, partners and motorists nationwide." },
            ].map((item, index) => (
              <AnimatedSection key={item.title} delay={index * 0.08}>
                <div className="glass-card p-8 h-full group hover:border-martimma-yellow/20 transition-colors">
                  <div className="w-10 h-10 rounded-lg bg-martimma-red/10 flex items-center justify-center mb-4">
                    <span className="text-martimma-red font-bold">{String(index + 1).padStart(2, '0')}</span>
                  </div>
                  <h3 className="text-white font-semibold mb-3">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}
